// Rule-set approval and activation (0023_policy_activation.sql): the texts and lists the
// server uses, mirrored for the Rules page, the SOP 22 generator and the tests. No React,
// no DOM, explicit .ts imports only (Node loads it for scripts/build-docs.ts).
//
// The server decides everything; these constants only describe it. tests/db/policy-activation
// checks they match private.implemented_routes() and the migration's declaration texts.

/** Permit classes the software implements (private.implemented_permit_classes). */
export const IMPLEMENTED_PERMIT_CLASSES: readonly string[] = ['A', 'B']

/**
 * Routes the software implements (private.implemented_routes), with the values each needs.
 * A route with a permitClass needs its values only in a version that enables that class (0030).
 */
export const IMPLEMENTED_ROUTES: readonly { code: string; label: string; requiredParameters: readonly string[]; permitClass?: string }[] = [
  {
    code: 'class_b_lifecycle',
    label:
      'Class B permit lifecycle: application, review, authorisation, Operations Manager approval, daily cycle with gas tests and toolbox talk, stops, cancellation, closeout and archive',
    requiredParameters: [
      'permit_classes_supported',
      'routes.unsupported',
      'class_b.authorisation_max_days',
      'class_b.notice_min_hours',
      'validation.normal_hours',
      'validation.max_lead_minutes',
      'holder_capacity',
      'work_party.level2_trigger_persons',
      'role_routes',
      'separation.prohibited_pairs',
      'separation.exception_only_pairs',
      'examinations.class_b',
      'gas.acceptance_mode',
      'gas.reference_method',
      'gas.species_units',
      'gas.acceptance_requires_completed_test',
      'supporting_documents.routes',
      'procedure_verification.route',
      'roles.area_authority_duties',
      'approvals.om_required',
    ],
  },
  {
    code: 'class_a_lifecycle',
    permitClass: 'A',
    label:
      'Class A permit lifecycle: the Class B lifecycle plus Production Coordinator approval before authorisation, a worksite examination at every validation in a process facility, the validating RS staying in the work area, and the first gas test of each day by the validating RS or a recorded delegation',
    requiredParameters: [
      'permit_classes_supported',
      'routes.unsupported',
      'class_a.authorisation_max_days',
      'class_a.notice_min_hours',
      'class_a.extension_allowance_days',
      'examinations.class_a',
      'gas.class_a_first_test_delegation',
      'holder_capacity',
      'role_routes',
      'separation.prohibited_pairs',
    ],
  },
  { code: 'recall', label: 'Recall of a submitted application by its Permit Applicant', requiredParameters: [] },
  {
    code: 'extension',
    label: 'Extension of the authorised period: the PA or PH asks, the RS approves or refuses, within the class extension allowance',
    requiredParameters: ['class_b.extension_allowance_days'],
  },
  {
    code: 'isolation',
    label:
      'Isolation declared on the application (kind, reason, planned period, certificate), the Isolation Certificate recorded and verified as a supporting document before authorisation, and its disposition at closeout',
    requiredParameters: ['supporting_documents.routes'],
  },
]

/** Route codes the software does not implement; an operational version lists all of them in routes.unsupported. */
export const UNIMPLEMENTED_ROUTES: readonly string[] = [
  'task_sop_now',
  'no_permit_job',
  'confined_space_entry',
  'excavation',
  'vehicle_entry',
  'hot_work',
  'level2_jhsep',
  'handover',
  'alarm_emergency_suspension',
  'remote_suspension',
  'paper_import',
]

/** Same-permit pairs every operational version must prohibit (0016 decision 5). */
export const REQUIRED_SEPARATION_PAIRS: readonly (readonly [string, string])[] = [
  ['RS', 'PA'],
  ['RS', 'PH'],
  ['OM', 'RS'],
  ['OM', 'PA'],
  ['OM', 'PH'],
]

/** Pairs a version that issues Class A must also prohibit (private.class_a_required_separation_pairs, 0030). */
export const CLASS_A_REQUIRED_SEPARATION_PAIRS: readonly (readonly [string, string])[] = [
  ['PC', 'PA'],
  ['PC', 'PH'],
  ['PC', 'RS'],
]

/** The only gas acceptance model the software implements. */
export const IMPLEMENTED_GAS_MODEL = 'agt_declared_against_reference'

/** What the freeze, the review/approval record, the activation and the withdrawal declare (server texts). */
export const FREEZE_DECLARATION =
  'I freeze this candidate rule version. Its values can no longer change; the reviewer and the approver review exactly the rules fingerprint shown.'
export const APPROVAL_RECORD_DECLARATION =
  'I record this decision exactly as the referenced document states it. The person named made the decision; recording it gives me no approval authority.'
export const ACTIVATION_DECLARATION =
  'I activate this approved rule version for new permits at the issuing authorities chosen, from the date shown. Permits already raised keep the version they were raised under.'
export const WITHDRAWAL_DECLARATION =
  'I withdraw this rule version. No new permit uses it, and permits governed by it are blocked until they are replaced.'

export type PolicyLifecycle = 'candidate' | 'frozen' | 'approved' | 'scheduled' | 'active' | 'superseded' | 'withdrawn' | 'synthetic'
type Tone = 'ok' | 'info' | 'synthetic' | 'bad' | 'warn' | 'neutral'

export const LIFECYCLE_INFO: Record<PolicyLifecycle, { label: string; tone: Tone; explanation: string }> = {
  candidate: {
    label: 'Candidate',
    tone: 'info',
    explanation: 'Being prepared. Its values can still change. It governs no permit.',
  },
  frozen: {
    label: 'Frozen - awaiting approval',
    tone: 'info',
    explanation: 'Its values and fingerprint are fixed for review and approval. It governs no permit yet.',
  },
  approved: {
    label: 'Approved - not active',
    tone: 'warn',
    explanation: 'Reviewed and approved, but not activated at any issuing authority yet.',
  },
  scheduled: {
    label: 'Approved - activation scheduled',
    tone: 'warn',
    explanation: 'Approved and activated from a later date. New permits use it from then.',
  },
  active: {
    label: 'Active',
    tone: 'ok',
    explanation: 'New permits at the issuing authorities shown are raised under this version. Older permits keep theirs.',
  },
  superseded: {
    label: 'Superseded',
    tone: 'neutral',
    explanation: 'A later version is active for new permits. Permits raised under this one keep it.',
  },
  withdrawn: {
    label: 'Withdrawn',
    tone: 'bad',
    explanation: 'No new permit uses it, and permits governed by it are blocked.',
  },
  synthetic: {
    label: 'Synthetic (demonstration only)',
    tone: 'synthetic',
    explanation: 'A demonstration rule set. It is not policy and can never be approved or activated.',
  },
}

/** Plain titles for the blocker codes the capability check and activation return. */
export const CAPABILITY_BLOCKER_TITLES: Record<string, string> = {
  VERSION_NOT_FOUND: 'Version not found',
  VERSION_SYNTHETIC: 'Synthetic version',
  SYNTHETIC_VALUES: 'Synthetic values',
  ROLE_DUTIES_NOT_EXPLICIT: 'RS duties not stated explicitly',
  OM_APPROVAL_NOT_EXPLICIT: 'OM approval not stated explicitly',
  REQUIRED_VALUE_MISSING: 'A built route needs a missing value',
  REQUIRED_VALUE_UNRESOLVED: 'A built route needs an unresolved value',
  REQUIRED_VALUE_NOT_APPROVED: 'A built route needs a value that is only pending approval',
  PARAMETER_INVALID: 'Value in the wrong form',
  NO_PERMIT_CLASS: 'No permit class',
  CLASS_NOT_IMPLEMENTED: 'Permit class not built',
  ROUTE_NOT_IMPLEMENTED: 'Route not built',
  IMPLEMENTED_ROUTE_LISTED_UNSUPPORTED: 'Built route listed as unsupported',
  AREA_AUTHORITY_ROUTE_PRESENT: 'Area Authority route present',
  ROLE_ROUTE_MISSING: 'Role route missing',
  ROLE_ROUTE_NOT_RECORDABLE: 'Role route the licence register cannot satisfy',
  ROLE_SEPARATION_INCOMPLETE: 'Role separation incomplete',
  EXAMINATION_ROUTE_NOT_IMPLEMENTED: 'Daily examination not built',
  CLASS_A_LISTED_UNSUPPORTED: 'Class A enabled and listed as unsupported',
  CLASS_A_HCA_STAYS_BLOCKED: 'Class A in hydrocarbon areas stays blocked',
  GAS_MODEL_NOT_IMPLEMENTED: 'Gas acceptance model not built',
  GAS_REFERENCE_METHOD_PLACEHOLDER: 'Gas reference method is a placeholder',
  GAS_NUMERIC_LIMITS_NOT_ENFORCED: 'Numeric gas limits would not be enforced',
  ENVIRONMENT_NOT_OPERATIONAL: 'Not the operational environment',
  VERSION_NOT_APPROVED: 'Not approved',
  VERSION_WITHDRAWN: 'Withdrawn',
  FREEZE_MISMATCH: 'Values differ from the approved freeze',
  EFFECTIVE_FROM_IN_PAST: 'Effective date in the past',
  EFFECTIVE_BEFORE_APPROVAL: 'Before the approved effective date',
  AUTHORITY_REQUIRED: 'No issuing authority chosen',
  AUTHORITY_NOT_FOUND: 'Issuing authority not found',
  AUTHORITY_NOT_OWN_ISSUE: 'Client permit system governs',
  AUTHORITY_REVOKED: 'Issuing authority revoked',
  AUTHORITY_ENDS_BEFORE_ACTIVATION: 'Issuing authority ends first',
  AUTHORITY_ISSUER_MISMATCH: 'Another organisation',
  AUTHORITY_SITE_NOT_COMPANY_OWNED: 'Company-wide licences would not cover the site',
  ALREADY_ACTIVE: 'Already active',
  LATER_ACTIVATION_SCHEDULED: 'A later activation is scheduled',
  ROUTE_STAYS_BLOCKED: 'Stays blocked',
  LICENCE_WITHOUT_EXPIRY_BLOCKS: 'Licences without expiry do not count',
  UNIT_CUSTODIAN_MISSING: 'Unit without a responsible RS',
}

/** The capability-check items, in the order SOP 22 and the Rules page explain them. */
export const CAPABILITY_CHECK_ITEMS: readonly string[] = [
  'It is not a synthetic demonstration version and holds no synthetic value.',
  'It states roles.area_authority_duties = "RS" and approvals.om_required = true as approved values; the historic Area Authority and no-OM defaults never apply.',
  'Every value a built route needs is present and approved; an unresolved value only blocks a route that is not built (and that route stays blocked).',
  'It enables only permit classes and routes the software implements (today Class B with recall, extension and isolation recorded on the application), lists every other route as unsupported and lists no built route as unsupported.',
  'Its role routes accept only roles the licence register records, with no Area Authority, and it prohibits RS+PA, RS+PH, OM+RS, OM+PA and OM+PH on one permit.',
  'Its gas acceptance model is the gas tester\'s declaration against an approved reference method; numeric comparison is not built, so a numeric model or numeric limits are refused.',
]

export type ApprovalKind = 'review' | 'approval'

/** The review/approval evidence form. Always starts empty: nothing is prefilled from the signed-in person. */
export type ApprovalForm = {
  approval_kind: ApprovalKind
  approver_name: string
  approver_role_title: string
  approved_on: string
  document_reference: string
  document_revision: string
  effective_from: string
  evidence_location: string
  notes: string
  declaration_confirmed: boolean
}

export function emptyApprovalForm(kind: ApprovalKind): ApprovalForm {
  return {
    approval_kind: kind,
    approver_name: '',
    approver_role_title: '',
    approved_on: '',
    document_reference: '',
    document_revision: '',
    effective_from: '',
    evidence_location: '',
    notes: '',
    declaration_confirmed: false,
  }
}

/** Field problems the form shows before sending (the server checks again). */
export function approvalFormErrors(f: ApprovalForm, today: string): Partial<Record<keyof ApprovalForm, string>> {
  const e: Partial<Record<keyof ApprovalForm, string>> = {}
  const who = f.approval_kind === 'review' ? 'reviewed' : 'approved'
  if (f.approver_name.trim().length < 2) e.approver_name = `The full name of the person who ${who}, as the document states it.`
  if (f.approver_role_title.trim().length < 2) e.approver_role_title = 'Their position as the document states it, for example HSE Manager or CEO.'
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.approved_on)) e.approved_on = 'The date of the decision.'
  else if (f.approved_on > today) e.approved_on = 'The decision date cannot be in the future.'
  if (!f.document_reference.trim()) e.document_reference = 'The reference of the signed document.'
  if (!f.document_revision.trim()) e.document_revision = 'The revision of the signed document.'
  if (f.approval_kind === 'approval' && !f.effective_from) e.effective_from = 'The effective date the approval states.'
  if (!f.declaration_confirmed) e.declaration_confirmed = 'Confirm the recording declaration.'
  return e
}
